// Login page — branded Cognito SRP form with TOTP MFA.
//
// Reference: PRPs/phone_lookup_rtb_api_prp_v2.md §13.2 (Login page),
// Locked L19 (MFA TOTP).
//
// Flow: email/password → (a) ok / (b) MFA challenge / (c) MFA enrollment.
//
// Anti-patterns explicitly avoided here:
//   - No persistent storage of the typed password — cleared on every state
//     transition.
//   - No QR-image fetched from a third party — we render the otpauth URI
//     verbatim (most authenticator apps accept manual entry / scan-from-link).

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { CognitoUser } from "amazon-cognito-identity-js";

import { useAuth } from "../lib/auth";

interface FromLocationState {
  from?: { pathname?: string };
}

type Step =
  | { kind: "credentials" }
  | { kind: "mfaChallenge"; cognitoUser: CognitoUser }
  | { kind: "mfaSetup"; cognitoUser: CognitoUser; secret: string };

export default function Login() {
  const { user, signIn, verifyTotp, associateTotp, verifyAndSetTotp } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<Step>({ kind: "credentials" });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      const state = (location.state ?? {}) as FromLocationState;
      const dest = state.from?.pathname ?? "/";
      navigate(dest, { replace: true });
    }
  }, [user, location.state, navigate]);

  const onSubmitCredentials = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn(username, password);
      if (result.kind === "ok") {
        const state = (location.state ?? {}) as FromLocationState;
        navigate(state.from?.pathname ?? "/", { replace: true });
      } else if (result.kind === "mfaRequired") {
        setStep({ kind: "mfaChallenge", cognitoUser: result.cognitoUser });
        setCode("");
      } else if (result.kind === "mfaSetupRequired") {
        try {
          const secret = await associateTotp(result.cognitoUser);
          setStep({
            kind: "mfaSetup",
            cognitoUser: result.cognitoUser,
            secret,
          });
          setCode("");
        } catch (err) {
          setError(err instanceof Error ? err.message : "MFA setup failed");
        }
      } else if (result.kind === "newPasswordRequired") {
        setError(
          "A new password is required. Set one via the AWS console (admin-set-user-password --permanent).",
        );
      } else {
        setError(result.message);
      }
    } finally {
      setPassword("");
      setSubmitting(false);
    }
  };

  const onSubmitMfaChallenge = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (step.kind !== "mfaChallenge") return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await verifyTotp(step.cognitoUser, code.trim());
      if (result.kind === "ok") {
        const state = (location.state ?? {}) as FromLocationState;
        navigate(state.from?.pathname ?? "/", { replace: true });
      } else if (result.kind === "error") {
        setError(result.message);
      }
    } finally {
      setCode("");
      setSubmitting(false);
    }
  };

  const onSubmitMfaSetup = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (step.kind !== "mfaSetup") return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await verifyAndSetTotp(
        step.cognitoUser,
        code.trim(),
        "Loop RTB Admin",
      );
      if (result.kind === "ok") {
        const state = (location.state ?? {}) as FromLocationState;
        navigate(state.from?.pathname ?? "/", { replace: true });
      } else if (result.kind === "error") {
        setError(result.message);
      }
    } finally {
      setCode("");
      setSubmitting(false);
    }
  };

  const otpauthUri =
    step.kind === "mfaSetup"
      ? `otpauth://totp/Loop%20RTB%20Admin:${encodeURIComponent(username)}?secret=${step.secret}&issuer=Loop%20RTB%20Admin`
      : null;

  return (
    <div style={shell}>
      <div style={card}>
        <header style={{ textAlign: "center", marginBottom: 24 }}>
          <img src="/logo.png" alt="Loop" style={logoStyle} />
          <h1 style={titleStyle}>Loop RTB</h1>
          <p style={{ margin: 0, color: "#9494a8", fontSize: 14 }}>
            Operator console
          </p>
        </header>

        {step.kind === "credentials" && (
          <form onSubmit={onSubmitCredentials} aria-label="Sign in">
            <Field label="Email" htmlFor="login-username">
              <input
                id="login-username"
                name="username"
                type="email"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                style={input}
              />
            </Field>
            <Field label="Password" htmlFor="login-password">
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={input}
              />
            </Field>
            {error ? <Alert>{error}</Alert> : null}
            <button type="submit" disabled={submitting} style={primaryBtn}>
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}

        {step.kind === "mfaChallenge" && (
          <form onSubmit={onSubmitMfaChallenge} aria-label="MFA code">
            <p style={hint}>
              Enter the 6-digit code from your authenticator app.
            </p>
            <Field label="Authentication code" htmlFor="login-totp">
              <input
                id="login-totp"
                name="totp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
                style={{ ...input, letterSpacing: "0.4em", textAlign: "center" }}
              />
            </Field>
            {error ? <Alert>{error}</Alert> : null}
            <button type="submit" disabled={submitting} style={primaryBtn}>
              {submitting ? "Verifying…" : "Verify"}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep({ kind: "credentials" });
                setError(null);
                setCode("");
              }}
              style={secondaryBtn}
            >
              Back
            </button>
          </form>
        )}

        {step.kind === "mfaSetup" && (
          <form onSubmit={onSubmitMfaSetup} aria-label="MFA setup">
            <p style={hint}>
              Add this account to your authenticator app, then enter the
              6-digit code below.
            </p>
            <div style={mfaBox}>
              <div style={{ fontSize: 12, color: "#9494a8", marginBottom: 6 }}>
                One-tap link (mobile)
              </div>
              {otpauthUri ? (
                <a
                  href={otpauthUri}
                  style={{
                    color: "#2dffc4",
                    fontSize: 13,
                    wordBreak: "break-all",
                  }}
                >
                  Open in authenticator app
                </a>
              ) : null}
              <div
                style={{
                  fontSize: 12,
                  color: "#9494a8",
                  marginTop: 12,
                  marginBottom: 6,
                }}
              >
                Or enter manually
              </div>
              <code style={secretCode}>{step.secret}</code>
            </div>
            <Field label="Enter code from app" htmlFor="login-mfa-setup">
              <input
                id="login-mfa-setup"
                name="totpSetup"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
                style={{ ...input, letterSpacing: "0.4em", textAlign: "center" }}
              />
            </Field>
            {error ? <Alert>{error}</Alert> : null}
            <button type="submit" disabled={submitting} style={primaryBtn}>
              {submitting ? "Activating…" : "Activate authenticator"}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep({ kind: "credentials" });
                setError(null);
                setCode("");
              }}
              style={secondaryBtn}
            >
              Cancel
            </button>
          </form>
        )}
      </div>
      <p style={footerNote}>&copy; Loop &middot; staging</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiny styled atoms (inline-styles to keep zero-CSS-dep parity with the rest
// of the SPA — the design here is tailored, not framework-driven).
// ---------------------------------------------------------------------------

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <label
      htmlFor={htmlFor}
      style={{ display: "block", marginBottom: 14 }}
    >
      <div
        style={{
          fontSize: 12,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: "#9494a8",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

function Alert({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        background: "rgba(220, 38, 38, 0.08)",
        border: "1px solid rgba(220, 38, 38, 0.4)",
        color: "#fca5a5",
        padding: "10px 12px",
        borderRadius: 8,
        marginBottom: 14,
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

const shell: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "2rem 1rem",
  background:
    "radial-gradient(circle at 20% 0%, rgba(0,212,170,0.10) 0%, transparent 45%)," +
    "radial-gradient(circle at 80% 100%, rgba(0,212,170,0.06) 0%, transparent 45%)," +
    "#08080d",
  color: "#eeeef2",
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
};

const card: React.CSSProperties = {
  width: "100%",
  maxWidth: 400,
  padding: "2rem",
  background: "#14141d",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 16,
  boxShadow:
    "0 24px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,212,170,0.06)",
};

const logoStyle: React.CSSProperties = {
  width: 72,
  height: "auto",
  filter: "drop-shadow(0 12px 32px rgba(0,212,170,0.3))",
};

const titleStyle: React.CSSProperties = {
  margin: "12px 0 4px",
  fontSize: 22,
  fontWeight: 600,
  letterSpacing: "-0.01em",
  background: "linear-gradient(90deg, #2dffc4, #00d4aa)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

const input: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.08)",
  background: "#0c0c15",
  color: "#eeeef2",
  fontSize: 14,
  outline: "none",
};

const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: "11px 12px",
  marginTop: 4,
  borderRadius: 999,
  border: "none",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 14,
  background: "linear-gradient(135deg, #2dffc4, #00d4aa)",
  color: "#08080d",
  boxShadow: "0 6px 20px rgba(0,212,170,0.25)",
};

const secondaryBtn: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  marginTop: 8,
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "transparent",
  color: "#9494a8",
  cursor: "pointer",
  fontSize: 13,
};

const hint: React.CSSProperties = {
  margin: "0 0 16px",
  color: "#9494a8",
  fontSize: 13,
};

const mfaBox: React.CSSProperties = {
  background: "#0c0c15",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 10,
  padding: 14,
  marginBottom: 16,
};

const secretCode: React.CSSProperties = {
  display: "block",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12,
  background: "#08080d",
  color: "#2dffc4",
  padding: "10px 12px",
  borderRadius: 6,
  letterSpacing: "0.06em",
  wordBreak: "break-all",
};

const footerNote: React.CSSProperties = {
  marginTop: 24,
  color: "#555568",
  fontSize: 12,
};
